import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  buildNewOrderNotification,
  subscribeOnlineOrdersByRestaurant,
} from '@utilities/onlineOrders';
import type { OnlineOrder } from '@src/types/onlineOrder';
import { playOnlineOrderAlert } from './onlineOrderAlertSounds';
import type { AppNotification, ToastNotification } from './types';

const STORAGE_KEY = 'pos_app_notifications';
const MAX_STORED = 100;

interface NotificationContextValue {
  notifications: AppNotification[];
  unreadCount: number;
  toasts: ToastNotification[];
  watchedRestaurantId: string;
  onlineOrders: OnlineOrder[];
  ordersLoading: boolean;
  watchRestaurant: (restaurantId: string) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  dismissToast: (id: string) => void;
  openNotification: (notification: AppNotification) => void;
}

const NotificationContext = createContext<NotificationContextValue | null>(null);

function readStoredNotifications(): AppNotification[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as AppNotification[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeStoredNotifications(notifications: AppNotification[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(notifications.slice(0, MAX_STORED)));
  } catch {
    /* ignore quota errors */
  }
}

function upsertNotification(
  notifications: AppNotification[],
  incoming: AppNotification
): AppNotification[] {
  const existingIndex = notifications.findIndex((n) => n.id === incoming.id);
  if (existingIndex >= 0) {
    const next = [...notifications];
    next[existingIndex] = { ...next[existingIndex], ...incoming };
    return next;
  }
  return [incoming, ...notifications].slice(0, MAX_STORED);
}

export function NotificationProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<AppNotification[]>(() =>
    readStoredNotifications()
  );
  const [toasts, setToasts] = useState<ToastNotification[]>([]);
  const [onlineOrders, setOnlineOrders] = useState<OnlineOrder[]>([]);
  const [watchedRestaurantId, setWatchedRestaurantId] = useState('');
  const [ordersLoading, setOrdersLoading] = useState(false);

  const knownOrderIdsRef = useRef<Set<string>>(new Set());
  const isInitialSnapshotRef = useRef(true);
  const watchedRestaurantRef = useRef('');

  useEffect(() => {
    writeStoredNotifications(notifications);
  }, [notifications]);

  const pushToast = useCallback((toast: ToastNotification) => {
    setToasts((prev) => [...prev, toast]);
  }, []);

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const watchRestaurant = useCallback((restaurantId: string) => {
    if (!restaurantId || watchedRestaurantRef.current === restaurantId) return;
    watchedRestaurantRef.current = restaurantId;
    setWatchedRestaurantId(restaurantId);
    setOrdersLoading(true);
    knownOrderIdsRef.current = new Set();
    isInitialSnapshotRef.current = true;
  }, []);

  useEffect(() => {
    if (!watchedRestaurantId) {
      setOnlineOrders([]);
      setOrdersLoading(false);
      return;
    }

    const unsubscribe = subscribeOnlineOrdersByRestaurant(
      watchedRestaurantId,
      (orders) => {
        if (!isInitialSnapshotRef.current) {
          const newIncomingOrders = orders.filter(
            (order) => order.status === 'placed' && !knownOrderIdsRef.current.has(order.id)
          );

          if (newIncomingOrders.length > 0) {
            for (const order of newIncomingOrders) {
              const notification = buildNewOrderNotification(order);
              setNotifications((prev) => upsertNotification(prev, notification));

              pushToast({
                id: `toast:${notification.id}`,
                title: notification.title,
                body: notification.body,
                severity: 'info',
                actionLabel: 'View orders',
                orderCode: notification.orderCode,
                tableOrRoom: order.tableOrRoom,
                itemCount: order.itemCount,
                totalCost: order.totalCost,
                restaurantId: order.restaurantId,
                onAction: () => navigate(`/admin/${order.restaurantId}/online-orders`),
              });
            }

            playOnlineOrderAlert();
          }
        }

        for (const order of orders) {
          knownOrderIdsRef.current.add(order.id);
        }

        if (isInitialSnapshotRef.current) {
          isInitialSnapshotRef.current = false;
        }

        setOnlineOrders(orders);
        setOrdersLoading(false);
      },
      () => {
        setOrdersLoading(false);
      }
    );

    return unsubscribe;
  }, [watchedRestaurantId, pushToast, navigate]);

  const markAsRead = useCallback((id: string) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n))
    );
  }, []);

  const markAllAsRead = useCallback(() => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  }, []);

  const openNotification = useCallback(
    (notification: AppNotification) => {
      markAsRead(notification.id);
      navigate(`/admin/${notification.restaurantId}/online-orders`);
    },
    [markAsRead, navigate]
  );

  const unreadCount = useMemo(
    () => notifications.filter((n) => !n.read).length,
    [notifications]
  );

  const value = useMemo<NotificationContextValue>(
    () => ({
      notifications,
      unreadCount,
      toasts,
      watchedRestaurantId,
      onlineOrders,
      ordersLoading,
      watchRestaurant,
      markAsRead,
      markAllAsRead,
      dismissToast,
      openNotification,
    }),
    [
      notifications,
      unreadCount,
      toasts,
      watchedRestaurantId,
      onlineOrders,
      ordersLoading,
      watchRestaurant,
      markAsRead,
      markAllAsRead,
      dismissToast,
      openNotification,
    ]
  );

  return (
    <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>
  );
}

export function useNotifications(): NotificationContextValue {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
}
